/**
 * Browserseite der Morgenerinnerung: Service Worker, Berechtigung, Abo.
 *
 * Was der Server über ein Gerät weiss, ist genau das Abo-Objekt des
 * Browsers. Ob dieses Gerät angemeldet ist, erfährt die Oberfläche deshalb
 * vom Browser (`getSubscription`) und gleicht es mit dem Server ab — nicht
 * umgekehrt.
 */

export type PushSupport =
  | 'ok'
  /** iPhone/iPad im Browser: geht erst vom Home-Bildschirm aus. */
  | 'ios-install'
  | 'unsupported';

const isIos = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS meldet sich als Mac, hat aber einen Touchscreen.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export function pushSupport(): PushSupport {
  const capable =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (isIos() && !isStandalone()) return 'ios-install';
  return capable ? 'ok' : 'unsupported';
}

export const permission = (): NotificationPermission =>
  'Notification' in window ? Notification.permission : 'denied';

/** Wird beim Start einmal aufgerufen; ohne registrierten Worker kein Push. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => {
    // Ohne Worker fehlt nur die Erinnerung — die Anwendung läuft weiter.
  });
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready;
}

/**
 * Das Abo dieses Geräts, falls es eines gibt.
 *
 * `getRegistration` statt `ready`: ist der Worker nicht registriert (etwa
 * weil die Registrierung scheiterte), wartet `ready` ewig, und die Karte
 * bliebe beim Laden stehen.
 */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'ok') return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

/** VAPID-Schlüssel kommt als Base64url, `subscribe` will Bytes. */
function keyBytes(base64url: string): Uint8Array {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

/**
 * Fragt nach der Berechtigung und meldet das Gerät beim Push-Dienst an.
 *
 * Muss direkt aus einem Klick heraus laufen — Safari und Firefox fragen
 * sonst gar nicht erst.
 */
export async function subscribe(publicKey: string): Promise<PushSubscription | null> {
  const result = await Notification.requestPermission();
  if (result !== 'granted') return null;

  const reg = await registration();
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;

  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(publicKey) as BufferSource,
  });
}

/** Meldet das Gerät beim Push-Dienst ab und gibt den Endpunkt zurück. */
export async function unsubscribe(): Promise<string | null> {
  const sub = await currentSubscription();
  if (!sub) return null;
  const { endpoint } = sub;
  await sub.unsubscribe().catch(() => {});
  return endpoint;
}

/** Kurze Gerätebezeichnung, nur zur eigenen Wiedererkennung. */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /Android/.test(ua) ? 'Android'
    : isIos() ? 'iOS'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : 'Gerät';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser';
  return `${browser} auf ${os}`;
}
